import { useState, useEffect, useCallback, useRef } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { botAPI, marketAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Bot, Play, Square, Activity, TrendingUp, TrendingDown,
  AlertTriangle, DollarSign, BarChart3, Clock, Zap, Target,
  Shield, ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';

const FALLBACK_SYMBOLS = [
  { symbol: 'btc', name: 'Bitcoin', category: 'crypto' },
  { symbol: 'eth', name: 'Ethereum', category: 'crypto' },
  { symbol: 'gold', name: 'Gold', category: 'commodity' },
  { symbol: 'silver', name: 'Silver', category: 'commodity' },
  { symbol: 'eurusd', name: 'Euro / US Dollar', category: 'currency' },
];

const CATEGORY_LABELS = {
  crypto: 'Crypto', commodity: 'Commodities', currency: 'Forex',
  indices: 'Indices', stocks: 'Stocks', etfs: 'ETFs',
};

const STRATEGIES = [
  { value: 'instant', label: '⚡ Instant Execution', desc: 'Fires trade within seconds — no warm-up required' },
  { value: 'rsi', label: 'RSI Strategy', desc: 'Buy RSI<30, Sell RSI>70' },
  { value: 'ma_crossover', label: 'MA Crossover', desc: 'Short MA crosses Long MA' },
  { value: 'custom', label: 'Mean Reversion', desc: 'Buy below MA20, Sell above' },
];

function StatusBadge({ running }) {
  return (
    <span data-testid="bot-status-badge" className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${running ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
      <span className={`w-2 h-2 rounded-full ${running ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
      {running ? 'Active' : 'Inactive'}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-[var(--text-primary)]' }) {
  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-[11px] text-[var(--text-tertiary)] uppercase tracking-wide">{label}</span>
      </div>
      <p className={`text-xl font-bold font-mono ${color}`}>{value}</p>
      {sub && <p className="text-[10px] text-[var(--text-tertiary)] mt-1">{sub}</p>}
    </div>
  );
}

function LogEntry({ log }) {
  const levelColors = {
    info: 'text-blue-400 bg-blue-500/10',
    trade: 'text-green-400 bg-green-500/10',
    signal: 'text-amber-400 bg-amber-500/10',
    error: 'text-red-400 bg-red-500/10',
  };
  const cls = levelColors[log.level] || 'text-[var(--text-secondary)] bg-[var(--bg-primary)]';
  const time = new Date(log.timestamp).toLocaleTimeString('en-IN', { hour12: false });
  return (
    <div className="flex items-start gap-2 py-2 border-b border-[var(--border-subtle)] last:border-0">
      <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${cls}`}>{log.level}</span>
      <span className="text-[11px] text-[var(--text-tertiary)] font-mono shrink-0">{time}</span>
      <span className="text-xs text-[var(--text-secondary)] break-all">{log.message}</span>
    </div>
  );
}

export default function RoboTradingPage() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showLogs, setShowLogs] = useState(true);
  const [showTrades, setShowTrades] = useState(false);
  const [symbols, setSymbols] = useState(FALLBACK_SYMBOLS);
  const pollRef = useRef(null);

  const [form, setForm] = useState({
    symbol: 'btc', symbol_name: 'Bitcoin', market_category: 'crypto',
    strategy: 'rsi', trade_amount: 100, stop_loss_pct: 2,
    take_profit_pct: 5, max_trades_per_day: 10, daily_loss_limit: 500, cooldown_seconds: 10,
  });

  const fetchAll = useCallback(async () => {
    try {
      const [sRes, lRes, tRes] = await Promise.all([
        botAPI.getStatus(), botAPI.getLogs(30), botAPI.getTrades(20),
      ]);
      setStatus(sRes.data);
      setLogs(lRes.data);
      setTrades(tRes.data);
      if (sRes.data.settings) {
        setForm(prev => ({
          ...prev,
          symbol: sRes.data.settings.symbol || prev.symbol,
          symbol_name: sRes.data.settings.symbol_name || prev.symbol_name,
          market_category: sRes.data.settings.market_category || prev.market_category,
          strategy: sRes.data.settings.strategy || prev.strategy,
          trade_amount: sRes.data.settings.trade_amount ?? prev.trade_amount,
          stop_loss_pct: sRes.data.settings.stop_loss_pct ?? prev.stop_loss_pct,
          take_profit_pct: sRes.data.settings.take_profit_pct ?? prev.take_profit_pct,
          max_trades_per_day: sRes.data.settings.max_trades_per_day ?? prev.max_trades_per_day,
          daily_loss_limit: sRes.data.settings.daily_loss_limit ?? prev.daily_loss_limit,
          cooldown_seconds: sRes.data.settings.cooldown_seconds ?? prev.cooldown_seconds,
        }));
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Load the full tradable universe (crypto + forex + commodities + indices + stocks + ETFs)
  useEffect(() => {
    (async () => {
      try {
        const [catRes, coinRes] = await Promise.all([
          marketAPI.getCategories(),
          marketAPI.getTopCoins(20).catch(() => ({ data: [] })),
        ]);
        const list = [];
        (coinRes.data || []).forEach(c => {
          if (c.symbol) list.push({ symbol: String(c.symbol).toLowerCase(), name: c.name || c.symbol, category: 'crypto' });
        });
        Object.entries(catRes.data || {}).forEach(([cat, items]) => {
          (items || []).forEach(i => {
            if (i.id) list.push({ symbol: String(i.id).toLowerCase(), name: i.name || i.displayName || i.id, category: cat });
          });
        });
        if (list.length) setSymbols(list);
      } catch (e) { /* keep fallback list */ }
    })();
  }, []);

  // Polling — faster when the bot is active, slow otherwise (picks up admin changes)
  useEffect(() => {
    pollRef.current = setInterval(fetchAll, status?.is_running ? 5000 : 15000);
    return () => clearInterval(pollRef.current);
  }, [status?.is_running, fetchAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await botAPI.saveSettings(form);
      toast.success('Bot settings saved');
      await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleToggle = async () => {
    setToggling(true);
    try {
      if (status?.is_running) {
        await botAPI.stop();
        toast.success('Bot stopped');
      } else {
        // Save settings first, then start
        await botAPI.saveSettings(form);
        await botAPI.start();
        toast.success('Bot started — monitoring market...');
      }
      await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setToggling(false);
  };

  const handleSymbolChange = (val) => {
    const found = symbols.find(s => s.symbol === val);
    if (found) setForm(f => ({ ...f, symbol: found.symbol, symbol_name: found.name, market_category: found.category }));
  };

  const roboAllowed = status?.robo_allowed !== false;
  const isRunning = status?.is_running;
  const indicators = status?.indicators || {};
  const openTrade = status?.open_trade;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-6 h-6 animate-spin text-[var(--text-tertiary)]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="robo-trading-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-lg border border-cyan-500/20">
              <Bot className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[var(--text-primary)]">Robo Trading</h1>
              <p className="text-xs text-[var(--text-tertiary)]">Automated trading bot with strategy-based execution</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge running={isRunning} />
            <Button
              data-testid="bot-toggle-btn"
              onClick={handleToggle}
              disabled={toggling || (!isRunning && !roboAllowed)}
              className={isRunning
                ? 'bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25'
                : 'bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25'
              }
            >
              {toggling ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : isRunning ? <Square className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
              {isRunning ? 'Stop Bot' : 'Start Bot'}
            </Button>
          </div>
        </div>

        {!roboAllowed && (
          <div data-testid="robo-blocked-banner"
            className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/25">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
            <p className="text-xs text-red-400">
              {status?.robo_blocked_reason || 'Robo trading is currently disabled by the administrator.'}
            </p>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Activity} label="Today's Trades" value={status?.today_trades || 0} sub={`Max: ${form.max_trades_per_day}/day`} color="text-cyan-400" />
          <StatCard icon={BarChart3} label="Total Trades" value={status?.total_trades || 0} color="text-blue-400" />
          <StatCard icon={DollarSign} label="Total P/L" value={`${(status?.total_pnl || 0) >= 0 ? '+' : ''}$${(status?.total_pnl || 0).toFixed(2)}`} color={(status?.total_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'} />
          <StatCard icon={Target} label="Strategy" value={STRATEGIES.find(s => s.value === form.strategy)?.label || form.strategy} sub={form.symbol.toUpperCase()} color="text-amber-400" />
        </div>

        {/* Indicators + Open Trade */}
        {isRunning && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Live Indicators */}
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg p-4">
              <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-400" /> Live Indicators
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                  <p className="text-[10px] text-[var(--text-tertiary)] uppercase">RSI (14)</p>
                  <p className={`text-lg font-bold font-mono ${indicators.rsi != null ? (indicators.rsi < 30 ? 'text-green-400' : indicators.rsi > 70 ? 'text-red-400' : 'text-[var(--text-primary)]') : 'text-[var(--text-tertiary)]'}`}>
                    {indicators.rsi != null ? indicators.rsi : (indicators.candle_count > 0 ? 'Building...' : 'Waiting...')}
                  </p>
                </div>
                <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                  <p className="text-[10px] text-[var(--text-tertiary)] uppercase">Price Samples</p>
                  <p className="text-lg font-bold font-mono text-[var(--text-primary)]">{indicators.candle_count || 0}</p>
                </div>
                <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                  <p className="text-[10px] text-[var(--text-tertiary)] uppercase">MA Short (10)</p>
                  <p className="text-sm font-bold font-mono text-[var(--text-primary)]">
                    {indicators.ma_short ? `$${Number(indicators.ma_short).toLocaleString()}` : '—'}
                  </p>
                </div>
                <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                  <p className="text-[10px] text-[var(--text-tertiary)] uppercase">MA Long (20)</p>
                  <p className="text-sm font-bold font-mono text-[var(--text-primary)]">
                    {indicators.ma_long ? `$${Number(indicators.ma_long).toLocaleString()}` : '—'}
                  </p>
                </div>
              </div>
            </div>

            {/* Open Trade */}
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg p-4">
              <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4 text-cyan-400" /> Current Trade
              </h3>
              {openTrade ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {openTrade.trade_type === 'buy'
                        ? <TrendingUp className="w-5 h-5 text-green-400" />
                        : <TrendingDown className="w-5 h-5 text-red-400" />}
                      <span className="text-sm font-bold text-[var(--text-primary)]">{openTrade.trade_type.toUpperCase()} {openTrade.symbol.toUpperCase()}</span>
                    </div>
                    <span className={`text-sm font-bold font-mono ${(openTrade.current_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {(openTrade.current_pnl || 0) >= 0 ? '+' : ''}${(openTrade.current_pnl || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="p-2 bg-[var(--bg-primary)] rounded">
                      <p className="text-[var(--text-tertiary)]">Entry</p>
                      <p className="font-mono text-[var(--text-primary)]">${openTrade.entry_price?.toLocaleString()}</p>
                    </div>
                    <div className="p-2 bg-[var(--bg-primary)] rounded">
                      <p className="text-[var(--text-tertiary)]">SL</p>
                      <p className="font-mono text-red-400">${openTrade.stop_loss?.toLocaleString()}</p>
                    </div>
                    <div className="p-2 bg-[var(--bg-primary)] rounded">
                      <p className="text-[var(--text-tertiary)]">TP</p>
                      <p className="font-mono text-green-400">${openTrade.take_profit?.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-24 text-[var(--text-tertiary)]">
                  <Clock className="w-6 h-6 mb-1 opacity-40" />
                  <p className="text-xs">No open trade — waiting for signal...</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Settings Panel */}
          <div className="lg:col-span-2 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg p-5 space-y-4" data-testid="bot-settings-panel">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-400" /> Bot Configuration
            </h3>

            <div className="space-y-3">
              <div>
                <Label className="text-xs text-[var(--text-secondary)] mb-1 block">Symbol</Label>
                <Select value={form.symbol} onValueChange={handleSymbolChange} disabled={isRunning || !roboAllowed}>
                  <SelectTrigger data-testid="bot-symbol-select" className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {Object.entries(
                      symbols.reduce((acc, s) => {
                        (acc[s.category] = acc[s.category] || []).push(s);
                        return acc;
                      }, {})
                    ).map(([cat, list]) => (
                      <div key={cat}>
                        <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-[var(--text-tertiary)]">{CATEGORY_LABELS[cat] || cat}</p>
                        {list.map(s => (
                          <SelectItem key={s.symbol} value={s.symbol}>{s.name} ({s.symbol.toUpperCase()})</SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-[var(--text-secondary)] mb-1 block">Strategy</Label>
                <Select value={form.strategy} onValueChange={v => setForm(f => ({ ...f, strategy: v }))} disabled={isRunning || !roboAllowed}>
                  <SelectTrigger data-testid="bot-strategy-select" className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STRATEGIES.map(s => (
                      <SelectItem key={s.value} value={s.value}>
                        <div className="flex flex-col">
                          <span>{s.label}</span>
                          <span className="text-[10px] text-[var(--text-tertiary)]">{s.desc}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[var(--text-secondary)] mb-1 block">Trade Amount ($)</Label>
                  <Input data-testid="bot-trade-amount" type="number" min="1" step="10" value={form.trade_amount}
                    onChange={e => setForm(f => ({ ...f, trade_amount: parseFloat(e.target.value) || 0 }))}
                    disabled={isRunning || !roboAllowed} className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono" />
                </div>
                <div>
                  <Label className="text-xs text-[var(--text-secondary)] mb-1 block">Max Trades/Day</Label>
                  <Input data-testid="bot-max-trades" type="number" min="1" max="100" value={form.max_trades_per_day}
                    onChange={e => setForm(f => ({ ...f, max_trades_per_day: parseInt(e.target.value) || 1 }))}
                    disabled={isRunning || !roboAllowed} className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[var(--text-secondary)] mb-1 block">Stop Loss (%)</Label>
                  <Input data-testid="bot-stop-loss" type="number" min="0.1" step="0.5" value={form.stop_loss_pct}
                    onChange={e => setForm(f => ({ ...f, stop_loss_pct: parseFloat(e.target.value) || 0 }))}
                    disabled={isRunning || !roboAllowed} className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono" />
                </div>
                <div>
                  <Label className="text-xs text-[var(--text-secondary)] mb-1 block">Take Profit (%)</Label>
                  <Input data-testid="bot-take-profit" type="number" min="0.1" step="0.5" value={form.take_profit_pct}
                    onChange={e => setForm(f => ({ ...f, take_profit_pct: parseFloat(e.target.value) || 0 }))}
                    disabled={isRunning || !roboAllowed} className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[var(--text-secondary)] mb-1 block">Daily Loss Limit ($)</Label>
                  <Input data-testid="bot-daily-loss" type="number" min="10" step="50" value={form.daily_loss_limit}
                    onChange={e => setForm(f => ({ ...f, daily_loss_limit: parseFloat(e.target.value) || 0 }))}
                    disabled={isRunning || !roboAllowed} className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono" />
                </div>
                <div>
                  <Label className="text-xs text-[var(--text-secondary)] mb-1 block">Cooldown (sec)</Label>
                  <Input data-testid="bot-cooldown" type="number" min="5" max="300" value={form.cooldown_seconds}
                    onChange={e => setForm(f => ({ ...f, cooldown_seconds: parseInt(e.target.value) || 10 }))}
                    disabled={isRunning || !roboAllowed} className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono" />
                </div>
              </div>
            </div>

            <Button data-testid="bot-save-settings-btn" onClick={handleSave} disabled={saving || isRunning || !roboAllowed} className="w-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/25">
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>

            {isRunning && (
              <p className="text-[10px] text-amber-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Stop the bot to modify settings
              </p>
            )}
          </div>

          {/* Logs + Trades */}
          <div className="lg:col-span-3 space-y-4">
            {/* Activity Logs */}
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg">
              <button onClick={() => setShowLogs(v => !v)} className="w-full flex items-center justify-between p-4 hover:bg-[var(--bg-primary)] rounded-t-lg transition-colors">
                <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-400" /> Activity Logs
                  <span className="text-[10px] text-[var(--text-tertiary)] font-normal">({logs.length})</span>
                </h3>
                {showLogs ? <ChevronUp className="w-4 h-4 text-[var(--text-tertiary)]" /> : <ChevronDown className="w-4 h-4 text-[var(--text-tertiary)]" />}
              </button>
              {showLogs && (
                <div className="px-4 pb-4 max-h-72 overflow-y-auto" data-testid="bot-logs-panel">
                  {logs.length > 0 ? logs.map((l, i) => <LogEntry key={l.id || i} log={l} />) : (
                    <p className="text-xs text-[var(--text-tertiary)] py-4 text-center">No logs yet. Start the bot to see activity.</p>
                  )}
                </div>
              )}
            </div>

            {/* Bot Trades */}
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg">
              <button onClick={() => setShowTrades(v => !v)} className="w-full flex items-center justify-between p-4 hover:bg-[var(--bg-primary)] rounded-t-lg transition-colors">
                <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-400" /> Bot Trades
                  <span className="text-[10px] text-[var(--text-tertiary)] font-normal">({trades.length})</span>
                </h3>
                {showTrades ? <ChevronUp className="w-4 h-4 text-[var(--text-tertiary)]" /> : <ChevronDown className="w-4 h-4 text-[var(--text-tertiary)]" />}
              </button>
              {showTrades && (
                <div className="px-4 pb-4 overflow-x-auto" data-testid="bot-trades-panel">
                  {trades.length > 0 ? (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[var(--text-tertiary)] text-left border-b border-[var(--border-subtle)]">
                          <th className="pb-2 font-medium">Type</th>
                          <th className="pb-2 font-medium">Symbol</th>
                          <th className="pb-2 font-medium">Amount</th>
                          <th className="pb-2 font-medium">Entry</th>
                          <th className="pb-2 font-medium">Exit</th>
                          <th className="pb-2 font-medium">P/L</th>
                          <th className="pb-2 font-medium">Status</th>
                          <th className="pb-2 font-medium">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trades.map((t, i) => (
                          <tr key={t.id || i} className="border-b border-[var(--border-subtle)] last:border-0">
                            <td className="py-2">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${t.trade_type === 'buy' ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
                                {t.trade_type?.toUpperCase()}
                              </span>
                            </td>
                            <td className="py-2 font-mono text-[var(--text-primary)]">{t.symbol?.toUpperCase()}</td>
                            <td className="py-2 font-mono text-[var(--text-secondary)]">${t.trade_amount?.toFixed(2)}</td>
                            <td className="py-2 font-mono text-[var(--text-secondary)]">${t.entry_price?.toLocaleString()}</td>
                            <td className="py-2 font-mono text-[var(--text-secondary)]">{t.exit_price ? `$${t.exit_price.toLocaleString()}` : '—'}</td>
                            <td className={`py-2 font-mono font-bold ${(t.profit_loss || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {t.status === 'closed' ? `${t.profit_loss >= 0 ? '+' : ''}$${t.profit_loss?.toFixed(2)}` : '—'}
                            </td>
                            <td className="py-2">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${t.status === 'open' ? 'bg-blue-500/15 text-blue-400' : 'bg-[var(--bg-primary)] text-[var(--text-secondary)]'}`}>
                                {t.status}
                              </span>
                            </td>
                            <td className="py-2 text-[var(--text-tertiary)]">{new Date(t.opened_at).toLocaleString('en-IN', { hour12: false, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-xs text-[var(--text-tertiary)] py-4 text-center">No bot trades yet.</p>
                  )}
                </div>
              )}
            </div>

            {/* Strategy Info */}
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg p-4">
              <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Strategy Reference</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                  <p className="font-semibold text-cyan-400 mb-1">RSI Strategy</p>
                  <p className="text-[var(--text-secondary)]">Buy when RSI &lt; 30 (oversold). Sell when RSI &gt; 70 (overbought). Period: 14.</p>
                </div>
                <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                  <p className="font-semibold text-amber-400 mb-1">MA Crossover</p>
                  <p className="text-[var(--text-secondary)]">Buy when MA10 crosses above MA20. Sell when MA10 crosses below MA20.</p>
                </div>
                <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                  <p className="font-semibold text-purple-400 mb-1">Mean Reversion</p>
                  <p className="text-[var(--text-secondary)]">Buy when price is 0.5% below MA20. Sell when price is 0.5% above MA20.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
